import { logger } from "@gik/analytics/utils/logger";
import { dotnetApi } from "@gik/core/api/ky/dotnetApi";
import DateTimeUtils from "@gik/core/utils/DateTimeUtils";
import type { IAddEditInkindUserUpdatesForm } from "@gik/inkind-page/components/AddEditInkindUserUpdatesModal/AddEditInkindUserUpdatesModal";
import { AddUserUpdateInputNames } from "@gik/inkind-page/enums/AddUserUpdateInputNames";
import type InkindUserUpdateMedia from "@gik/inkind-page/models/InkindUserUpdateMedia";
import moment from "moment";

const updateIdHeaderPropertyName = "location";

export interface IMediaData {
	data?: string;
	url?: string;
	thumbnailUrl?: string;
	caption?: string;
	id?: string;
}

export interface IUserUpdateData {
	title: string;
	description: string;
	publishedDate: string;
}

function getUserUpdatesUrl(inkindRouteId: string) {
	return `stream/inkinds/${inkindRouteId}/user-updates`;
}

function getUserUpdateUrl(inkindRouteId: string, userUpdateId: string) {
	return `${getUserUpdatesUrl(inkindRouteId)}/${userUpdateId}`;
}

export async function createUserUpdate(
	routeId: string,
	formData: Partial<IAddEditInkindUserUpdatesForm>,
	media: InkindUserUpdateMedia[],
): Promise<string> {
	let updateId: string;
	try {
		const response = await dotnetApi.post(getUserUpdatesUrl(routeId), {
			json: getUserUpdateTextData(formData),
		});
		// eslint-disable-next-line
		updateId =
			response.headers.get(updateIdHeaderPropertyName) ||
			((await response.json()) as any).id;
	} catch (e) {
		logger.error("Failed to create user update: ", e);
		return null;
	}

	try {
		await updateUserUpdateMedia(routeId, updateId, [], media);
	} catch (e) {
		logger.error("Failed to update user update media: ", e);
		return null;
	}

	if (formData[AddUserUpdateInputNames.SEND_OPTIONS_EMAIL]) {
		// Do not await sending email as Vero can take up to 10 seconds to send a successful response
		emailFollowers(routeId, updateId);
	}

	return updateId;
}

export async function updateUserUpdate(
	inkindRouteId: string,
	updateId: string,
	formData: Partial<IAddEditInkindUserUpdatesForm>,
) {
	try {
		await dotnetApi.put(getUserUpdateUrl(inkindRouteId, updateId), {
			json: getUserUpdateTextData(formData),
		});
	} catch (e) {
		logger.error("Failed to update user update: ", e);
		return;
	}
}

export async function updateUserUpdateMedia(
	inkindRouteId: string,
	updateId: string,
	mediaToBeRemoved: string[],
	media: InkindUserUpdateMedia[],
) {
	for (let i = 0; i < mediaToBeRemoved.length; i++) {
		const mediaId = mediaToBeRemoved[i];

		try {
			await removeMedia(inkindRouteId, updateId, mediaId);
		} catch (e) {
			logger.error("Failed to remove media: ", e);
			return;
		}
	}

	await addMedia(inkindRouteId, updateId, media);
}

async function addMedia(
	inkindRouteId: string,
	updateId: string,
	media: InkindUserUpdateMedia[],
) {
	for (let i = 1; i <= media.length; i++) {
		const m = media[i - 1];

		if (m.id) {
			continue;
		}

		if (m.type === "photo") {
			await addPhoto(inkindRouteId, updateId, {
				data: m.url,
				caption: m.caption,
			});
		} else {
			await addVideo(inkindRouteId, updateId, {
				caption: m.caption,
				thumbnailUrl: m.thumbnailUrl,
				url: m.url,
			});
		}
	}
}

async function addPhoto(
	inkindRouteId: string,
	updateId: string,
	photoData: IMediaData,
) {
	try {
		await dotnetApi.post(
			`${getUserUpdateUrl(inkindRouteId, updateId)}/photos`,
			{
				json: photoData,
			},
		);
	} catch (error) {
		logger.error(error);
	}
}

async function addVideo(
	inkindRouteId: string,
	updateId: string,
	videoData: IMediaData,
) {
	try {
		await dotnetApi.post(
			`${getUserUpdateUrl(inkindRouteId, updateId)}/videos`,
			{
				json: videoData,
			},
		);
	} catch (error) {
		logger.error(error);
	}
}

async function removeMedia(
	inkindRouteId: string,
	updateId: string,
	mediaId: string,
) {
	try {
		await dotnetApi.delete(
			`${getUserUpdateUrl(inkindRouteId, updateId)}/media/${mediaId}`,
		);
	} catch (error) {
		logger.error(error);
	}
}

export async function removeUpdate(inkindRouteId: string, updateId: string) {
	return await dotnetApi.delete(getUserUpdateUrl(inkindRouteId, updateId));
}

async function emailFollowers(inkindRouteId: string, updateId: string) {
	try {
		return await dotnetApi.post(
			`${getUserUpdateUrl(inkindRouteId, updateId)}/email-followers`,
		);
	} catch (e) {
		logger.error("Failed to email followers: ", e);
		return null;
	}
}
function getUserUpdateTextData(
	formData: Partial<IAddEditInkindUserUpdatesForm>,
): IUserUpdateData {
	return {
		title: formData?.[AddUserUpdateInputNames.TITLE],
		description: formData?.[AddUserUpdateInputNames.CONTENT],
		publishedDate: moment(formData?.[AddUserUpdateInputNames.DATE]).format(
			DateTimeUtils.DateFormat,
		),
	};
}
